import React, { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import useWizardManager from '@hooks/useWizardManager'
import routes from 'src/config/routes'
import { seenTreatment, completedTreatment } from 'src/services/analytics'
import { TreatmentScreen } from '@screens/TreatmentScreen/TreatmentScreen'
import { setInitialActionPerformed, useChoiceDispatch, useChoiceState } from '@context/ChoiceContext'
import { useVisibilityContext } from '@context/VisibilityContext'
import useCategorizedTreatments from '@hooks/useCategorizedTreatments'
import { useTranslation } from 'react-i18next'
import useTreatmentUpsellDetails from '@hooks/useTreatmentUpsellDetails'
import { findTreatmentInCategorizedTreatments } from 'src/helpers/treatment'
import { useSelectTreatment } from '@hooks/useSelectTreatment'
import { usePainDetailsContext } from '@context/PainDetailsContext'
import { useIsSuggestedTreatment } from '@hooks/useIsSuggestedTreatment'
import { getSuggestedTreatmentsCategory } from 'src/services/api/treatment'

export const ClinicTreatment = () => {
  const { isVisible } = useVisibilityContext()
  const { t } = useTranslation()
  useWizardManager({
    targetRoute: routes.treatment,
  })
  useEffect(() => {
    if (isVisible) {
      seenTreatment()
    }
  }, [isVisible])

  const { initialState, treatmentBundleTypeReferenceId } = useChoiceState()
  const dispatch = useChoiceDispatch()
  const history = useHistory()
  const { data: categorizedTreatments, isLoading: isLoadingTreatments } = useCategorizedTreatments()
  const { data: upsellDetails, isLoading: loadingUpsellDetails } = useTreatmentUpsellDetails()
  const { showPainTreatmentForm } = usePainDetailsContext()
  const [goToNextPage, setGoToNextPage] = useState(false)
  const handleNextClicked = useCallback(() => {
    setGoToNextPage(true)
  }, [])
  const selectTreatment = useSelectTreatment(handleNextClicked)

  useEffect(() => {
    if (goToNextPage && !loadingUpsellDetails) {
      completedTreatment()

      const redirectTo = (() => {
        if (showPainTreatmentForm) return routes.painDetails
        if (upsellDetails) return routes.upsell

        return routes.patient
      })()

      history.push(redirectTo)
    }
  }, [goToNextPage, loadingUpsellDetails, upsellDetails, showPainTreatmentForm, history])

  useEffect(() => {
    const treatment = findTreatmentInCategorizedTreatments(
      categorizedTreatments || [],
      initialState.treatmentBundleTypeReferenceId || ''
    )

    if (treatment && !initialState.initialActionPerformed) {
      selectTreatment(treatment.referenceId)
      dispatch(setInitialActionPerformed())
    }
    if (
      initialState?.treatmentBundleTypeReferenceId &&
      !isLoadingTreatments &&
      !treatment &&
      !initialState.initialActionPerformed
    ) {
      dispatch(setInitialActionPerformed())
      history.push(routes.bookByPhone)
    }
  }, [
    initialState?.treatmentBundleTypeReferenceId,
    initialState?.initialActionPerformed,
    categorizedTreatments,
    dispatch,
    history,
    isLoadingTreatments,
    t,
    selectTreatment,
  ])

  const isSuggestedTreatmentTest = useIsSuggestedTreatment()
  const categorizedTreatmentsToDisplay = isSuggestedTreatmentTest
    ? [getSuggestedTreatmentsCategory(categorizedTreatments), ...(categorizedTreatments || [])]
    : categorizedTreatments

  return (
    <TreatmentScreen
      categorizedTreatments={categorizedTreatmentsToDisplay}
      loading={isLoadingTreatments || loadingUpsellDetails}
      handleSelectTreatment={(id) => selectTreatment(id)}
      handleNextClicked={handleNextClicked}
      selectedTreatmentId={treatmentBundleTypeReferenceId}
    />
  )
}
